import type { CreatedEndpoint, EndpointInfo } from './api.js';

/**
 * The endpoints this browser has created, with their tokens.
 *
 * `localStorage` is the whole account system: there are no logins, and the API
 * only ever returns a token once. Losing this store means losing the endpoint,
 * which is why the UI says so out loud rather than hiding it.
 */

const KEY = 'mockapi.endpoints.v1';

export interface SavedEndpoint {
  slug: string;
  url: string;
  token: string;
  name: string | null;
  createdAt: string;
  expiresAt: string;
}

/**
 * Private-mode Safari throws on `localStorage` access rather than returning
 * null, and an iframe with a strict sandbox has no storage at all. Either way
 * the inspector should still work for the session, so every access is guarded.
 */
function store(): Storage | undefined {
  try {
    return window.localStorage;
  } catch {
    return undefined;
  }
}

export function listSaved(): SavedEndpoint[] {
  const raw = store()?.getItem(KEY);
  if (!raw) return [];

  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];

    // Anything without a slug and a token is unusable; drop it rather than let
    // a half-written entry break the whole list.
    return parsed.filter(
      (entry): entry is SavedEndpoint =>
        typeof entry === 'object' &&
        entry !== null &&
        typeof (entry as SavedEndpoint).slug === 'string' &&
        typeof (entry as SavedEndpoint).token === 'string',
    );
  } catch {
    return [];
  }
}

function write(endpoints: SavedEndpoint[]): void {
  try {
    store()?.setItem(KEY, JSON.stringify(endpoints));
  } catch {
    // Quota or a blocked store. The session keeps working; the list will just
    // not survive a reload, and the token warning already covers that risk.
  }
}

export function findSaved(slug: string): SavedEndpoint | undefined {
  return listSaved().find((endpoint) => endpoint.slug === slug);
}

/** Newest first, so the home page lists the one you just made at the top. */
export function saveEndpoint(endpoint: CreatedEndpoint): SavedEndpoint {
  const saved: SavedEndpoint = {
    slug: endpoint.slug,
    url: endpoint.url,
    token: endpoint.token,
    name: endpoint.name,
    createdAt: endpoint.createdAt,
    expiresAt: endpoint.expiresAt,
  };

  write([saved, ...listSaved().filter((entry) => entry.slug !== saved.slug)]);
  return saved;
}

/** Stores a token typed by hand, for an endpoint made in another browser. */
export function saveToken(slug: string, token: string, url: string): SavedEndpoint {
  const saved: SavedEndpoint = {
    slug,
    url,
    token,
    name: null,
    createdAt: new Date().toISOString(),
    expiresAt: new Date().toISOString(),
  };

  write([saved, ...listSaved().filter((entry) => entry.slug !== slug)]);
  return saved;
}

/** Refreshes the cached copy from what the server just told us. */
export function refreshSaved(endpoint: EndpointInfo): void {
  const current = listSaved();
  const index = current.findIndex((entry) => entry.slug === endpoint.slug);
  if (index === -1) return;

  const existing = current[index] as SavedEndpoint;
  current[index] = {
    ...existing,
    url: endpoint.url,
    name: endpoint.name,
    createdAt: endpoint.createdAt,
    expiresAt: endpoint.expiresAt,
  };
  write(current);
}

export function forgetEndpoint(slug: string): void {
  write(listSaved().filter((entry) => entry.slug !== slug));
}

/**
 * The rules editor used to keep its own single endpoint under its own key,
 * because it shipped before there was anywhere else to put it. Now that the
 * inspector owns the session, fold that entry into this list on the way past —
 * otherwise whoever was using it loses a token the API only ever handed out
 * once. Runs at most once: the old key is removed as it is read.
 */
const LEGACY_RULES_KEY = 'mock-api:rules-editor';

export function adoptLegacyRulesEndpoint(origin: string = window.location.origin): void {
  const storage = store();
  if (!storage) return;

  let raw: string | null;
  try {
    raw = storage.getItem(LEGACY_RULES_KEY);
  } catch {
    return;
  }
  if (!raw) return;

  try {
    const parsed: unknown = JSON.parse(raw);
    const legacy = parsed as { slug?: unknown; token?: unknown };

    if (typeof legacy.slug === 'string' && typeof legacy.token === 'string') {
      // Only if it is not already known: an entry saved here has a real
      // `expiresAt` from the server, which the legacy one never carried.
      if (!findSaved(legacy.slug)) {
        saveToken(legacy.slug, legacy.token, `${origin}/e/${legacy.slug}`);
      }
    }

    storage.removeItem(LEGACY_RULES_KEY);
  } catch {
    // Unreadable leftovers are not worth a broken page; drop them.
    try {
      storage.removeItem(LEGACY_RULES_KEY);
    } catch {
      // Nothing else to try.
    }
  }
}
